/**
 * Universal API error formatter.
 *
 * FastAPI returns Pydantic validation errors as `response.data.detail` being
 * an Array of objects. Template literals (`${detail}`) then stringify that as
 * `[object Object]`, which is the bug users were seeing on the ADR page.
 *
 * This helper handles every shape:
 *   - string detail
 *   - array of validation errors (Pydantic 422)
 *   - single object detail with msg/message
 *   - axios `e.response.data.message`
 *   - bare Error objects with .message
 *   - null / undefined
 *
 * Usage:
 *   import { formatApiError } from '@/utils/errorFormatter';
 *   toast.error(formatApiError(e, 'Default fallback'));
 */
export const formatApiError = (e, fallback = 'Request failed') => {
  if (!e) return fallback;
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const path = Array.isArray(d?.loc) ? d.loc.slice(1).join('.') : '';
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return path ? `${path}: ${msg}` : msg;
      })
      .slice(0, 3)
      .join(' · ');
  }
  if (detail && typeof detail === 'object') {
    const m = detail.msg || detail.message;
    if (m) return String(m);
    try {
      return JSON.stringify(detail).slice(0, 240);
    } catch {
      return fallback;
    }
  }
  if (e?.response?.data?.message) return String(e.response.data.message);
  if (e?.message) return String(e.message);
  return fallback;
};

/**
 * Format a raw `detail` value (already extracted from a fetch JSON body).
 * Use this when you already have the parsed body and just want to render
 * the `detail` field — e.g., after `await fetch(...).then(r => r.json())`.
 *
 * Example:
 *   const data = await res.json();
 *   toast.error(formatApiDetail(data.detail, 'Login failed'));
 */
export const formatApiDetail = (detail, fallback = 'Request failed') => {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail.trim() || fallback;
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const path = Array.isArray(d?.loc) ? d.loc.slice(1).join('.') : '';
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return path ? `${path}: ${msg}` : msg;
      })
      .slice(0, 3)
      .join(' · ');
  }
  if (typeof detail === 'object') {
    const m = detail.msg || detail.message;
    if (m) return String(m);
    try { return JSON.stringify(detail).slice(0, 240); } catch { return fallback; }
  }
  return String(detail);
};

export default formatApiError;
